import React, { useState, useMemo, useEffect, useRef } from 'react';
import CampaignStatsChart from './CampaignStatsChart';
import TreasureMap from './TreasureMap';
import BasketLocationModal from './BasketLocationModal';
import ConfirmModal from './ConfirmModal';
import { PeopleFill, HeartPulseFill, ChevronDown, ChevronRight, GeoAltFill, PencilFill, CheckLg, XLg } from 'react-bootstrap-icons';
import { setBasketHP, setBasketLocation, setCampaignEventTime, setBasketClose, setBasketOpen, setRewardCount } from '../services/api';
import LocalStorageManager from "./LocalStorageManager";

const ItemsTable = ({ items = [], searchTerm, statusFilter, isEditing = false, itemTotalDraft = {}, itemTotalApplied = {}, onDraftChange }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const normalizedItems = useMemo(() => {
    if (!items) return [];
    return items.map(item => ({
      itemname: item.itemname || item.itemName || '미정',
      rewardno: item.rewardno ?? item.rewardNo ?? '-',
      rewardkey: item.rewardkey ?? item.rewardKey,
      usable: item.usable,
      targetno: item.targetno ?? item.targetNo ?? item.lotvalue ?? item.lotValue,
      designatedHitNumber: item.designatedHitNumber ?? item.designatedhitnumber ?? item.designated_hit_number,
      isParticipationReward: item.isParticipationReward,
      status: item.status,
    }));
  }, [items]);

  const groupedItems = useMemo(() => {
    if (!normalizedItems || normalizedItems.length === 0) return [];
    const groups = normalizedItems.reduce((acc, item) => {
      acc[item.itemname] = acc[item.itemname] || { list: [], total: 0, given: 0, not_given: 0 };
      acc[item.itemname].list.push(item);
      acc[item.itemname].total++;
      if (item.usable === 0) {
        acc[item.itemname].given++;
      } else {
        acc[item.itemname].not_given++;
      }
      return acc;
    }, {});
    return Object.entries(groups).map(([name, data]) => ({ name, ...data }));
  }, [normalizedItems]);

  const filteredItems = useMemo(() => {
    return groupedItems
      .filter(group => group.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .filter(group => {
        if (statusFilter === 'given') return group.given > 0;
        if (statusFilter === 'not_given') return group.not_given > 0;
        return true;
      });
  }, [groupedItems, searchTerm, statusFilter]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    setExpandedGroup(null); // Collapse on page change
  };

  const toggleGroup = (groupName) => {
    setExpandedGroup(expandedGroup === groupName ? null : groupName);
  };

  if (normalizedItems.length === 0) {
    return <div className="alert alert-info mt-2">표시할 상품이 없습니다.</div>;
  }

  return (
    <div>
      <table className="table table-striped table-bordered table-hover mt-2" style={{ tableLayout: 'fixed' }}>
        <thead className="table-dark">
          <tr>
            <th style={{ width: '4%' }}></th>
            <th style={{ width: '31%' }}>상품명</th>
            <th style={{ width: '25%' }}>총 수량</th>
            <th style={{ width: '20%' }}>지급 완료</th>
            <th style={{ width: '20%' }}>미지급</th>
          </tr>
        </thead>
        <tbody>
          {paginatedItems.map(group => {
            const displayTotal = itemTotalApplied[group.name] ?? group.total;
            const draftVal = itemTotalDraft[group.name] ?? String(displayTotal);
            return (
            <React.Fragment key={group.name}>
              <tr onClick={() => !isEditing && toggleGroup(group.name)} style={{ cursor: isEditing ? 'default' : 'pointer' }}>
                <td>
                  {expandedGroup === group.name ? <ChevronDown /> : <ChevronRight />}
                </td>
                <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</td>
                <td onClick={(e) => isEditing && e.stopPropagation()}>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min="0"
                      value={draftVal}
                      onChange={(e) => onDraftChange(group.name, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : displayTotal}
                </td>
                <td>{group.given}</td>
                <td>{group.not_given}</td>
              </tr>
              {expandedGroup === group.name && (
                <tr>
                  <td colSpan="5">
                    <div style={{ padding: '1rem' }}>
                      <h5>{group.name} 상세 목록</h5>
                      <table className="table table-sm table-bordered">
                        <thead className="table-secondary">
                          <tr>
                            <th>순위</th>
                            <th>지정 타수</th>
                            <th>개별 상태</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.list.map(item => {
                            const rawHit = item.designatedHitNumber ?? item.targetno ?? item.targetNo ?? item.lotvalue ?? item.lotValue;
                            const isParticipation = item.isParticipationReward === true || rawHit === null || rawHit === undefined || Number(rawHit) === 0;
                            const displayHit = isParticipation ? '참가상' : rawHit;
                            return (
                              <tr key={item.rewardno}>
                                <td>{item.rewardno}</td>
                                <td>{displayHit}</td>
                                <td>{item.usable === 0 ? '지급 완료' : '미지급'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <nav className="d-flex justify-content-center">
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>이전</button>
            </li>
            {[...Array(totalPages).keys()].map(num => (
              <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => handlePageChange(num + 1)}>{num + 1}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>다음</button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

const TABS = ['진행 중', '시작 전', '종료'];

const changeBasketStatus = (status) => {
    switch (status) {
        case 0:
            return "터짐";
            break;
        case 2:
            return "2단계";
            break;
        case 3:
            return "3단계";
            break;
        default:
            return "1단계";
            break;
    }
};

const parseDateTime = (dt) => {
  if (!dt) return { date: '', time: '' };
  const d = new Date(dt);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = d.toTimeString().split(' ')[0];
  return { date, time };
};

const parseEventInfo = (event) => {
  const open  = parseDateTime(event.openDate);
  const start = parseDateTime(event.startDate);
  const end   = parseDateTime(event.endDate);
  const close = parseDateTime(event.closeDate);
    // console.log(event)

    return {
    openDate:  open.date,  openTime:  open.time,
    startDate: start.date, startTime: start.time,
    endDate:   end.date,   endTime:   end.time,
    closeDate: close.date, closeTime: close.time,
  };
};

const EVENT_INFO_LABELS = [
  { key: 'open',  label: '이벤트 오픈일시' },
  { key: 'start', label: '이벤트 시작일시' },
  { key: 'end',   label: '이벤트 종료일시' },
  { key: 'close', label: '이벤트 클로즈일시' },
];

const hasUndoForEventInfo = (eventKey) => {
  const data = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${eventKey}`));
  if (!data) return false;
  return ['openDate', 'startDate', 'endDate', 'closeDate'].some(k => data[k]?.backup != null);
};

const hasUndoForLocation = (eventKey) => {
  const data = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${eventKey}`));
  if (!data) return false;
  return ['longitude', 'latitude', 'distance'].some(k => data.baskets?.[k]?.backup != null);
};

const hasUndoForItems = (eventKey) => {
  const data = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${eventKey}`));
  if (!data || !data.items) return false;
  return Object.values(data.items).some(v => v?.backup != null);
};

const hasUndoForHp = (eventKey) => {
  const data = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${eventKey}`));
  return data?.baskets?.maxHP?.backup != null;
};

const RankedGourdBreakingReport = ({ rankedBoxEvents, luckyBoxEvents, totalSignups, campaignKey }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'given', 'not_given'
  const [activeTab, setActiveTab] = useState('진행 중');
  const [expandedEvents, setExpandedEvents] = useState({});
  const [hpPopoverBasket, setHpPopoverBasket] = useState(null);
  const [hpInputValue, setHpInputValue] = useState('');
  const [maxHpOverrides, setMaxHpOverrides] = useState({});
  const [editingEventInfo, setEditingEventInfo] = useState(null);
  const [eventInfoDraft, setEventInfoDraft] = useState({});
  const [eventInfoApplied, setEventInfoApplied] = useState({});
  const [isItemsEditing, setIsItemsEditing] = useState(false);
  const [itemTotalDraft, setItemTotalDraft] = useState({});
  const [itemTotalApplied, setItemTotalApplied] = useState({});
  const [basketLocationModal, setBasketLocationModal] = useState(null); // { basket, eventKey }
  const [basketLocationOverrides, setBasketLocationOverrides] = useState({}); // { [basketKey]: { lat, lng, distance } }
  const [confirmModal, setConfirmModal] = useState(null); // { onConfirm }
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const initializedRef = useRef(false);
  useEffect(() => {
    const evts = (rankedBoxEvents && rankedBoxEvents.length > 0 ? rankedBoxEvents : luckyBoxEvents) || [];
    if (evts.length === 0) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    evts.forEach(event => LocalStorageManager.get(event));
  }, [rankedBoxEvents, luckyBoxEvents]);

  const events = rankedBoxEvents && rankedBoxEvents.length > 0 ? rankedBoxEvents : (luckyBoxEvents || []);
  if (!events || events.length === 0) {
    return <p>표시할 박 터트리기 이벤트가 없습니다.</p>;
  }

  const toggleCollapse = (eventKey) => {
    setExpandedEvents(prev => ({ ...prev, [eventKey]: !prev[eventKey] }));
  };

  const filteredEvents = events.filter(e => e.statusName === activeTab);

  const getProgressBarVariant = (status) => {
    switch (status) {
        case 1:
            return 'bg-success';
        case 2:
            return 'bg-warning';
        default:
            return 'bg-danger';
    }
  };

  return (
    <div>
      <ul className="nav nav-tabs mb-4">
        {TABS.map(tab => (
          <li key={tab} className="nav-item">
            <button
              className={`nav-link ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
              <span className="badge ms-2 bg-secondary">{events.filter(e => e.statusName === tab).length}</span>
            </button>
          </li>
        ))}
      </ul>

      {filteredEvents.length === 0 && (
        <p className="text-muted">해당 상태의 이벤트가 없습니다.</p>
      )}

      {filteredEvents.map(event => {
        const isCollapsed = !expandedEvents[event.eventKey];

        return (
          <div key={event.eventKey} className="card mb-4 shadow-sm">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <h3 className="mb-0">{event.eventName} ({event.statusName})</h3>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0"
                  style={{ width: '36px', height: '36px', fontSize: '18px', flexShrink: 0 }}
                  title="박 상태 초기화"
                  onClick={() => setConfirmModal({ onConfirm: async () => { await setBasketOpen({ eventkey: event.eventKey }); setConfirmModal(null); } })}
                >🔄
                </button>
                  <button
                      className="btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0"
                      style={{ width: '36px', height: '36px', fontSize: '18px', flexShrink: 0 }}
                      title="즉시 터트리기"
                      onClick={() => setConfirmModal({ onConfirm: async () => { await setBasketClose({ eventkey: event.eventKey }); setConfirmModal(null); } })}
                  >💣
                  </button>
                <button
                  className="btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0"
                  style={{ width: '36px', height: '36px', flexShrink: 0 }}
                  onClick={() => toggleCollapse(event.eventKey)}
                  title={isCollapsed ? '펼치기' : '접기'}
                >
                  {isCollapsed ? <ChevronRight /> : <ChevronDown />}
                </button>
              </div>
            </div>
            {!isCollapsed && (
              <div className="card-body">
                <div className="row">
                  <div className="col-md-7 d-flex">
                    <div className="card shadow-sm flex-fill">
                      <div className="card-header">
                        <h4 className="mb-0"><PeopleFill className="me-2" />참여 현황</h4>
                      </div>
                      <div className="card-body">
                        <CampaignStatsChart
                          signupCount={totalSignups}
                          participantCount={event.participantCount}
                          todaySignups={event.todaySignups || 0}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <h4 className="mb-3"><HeartPulseFill className="me-2" />박 HP 정보</h4>
                    {event.baskets && event.baskets.map(basket => {
                      const hpOverride = maxHpOverrides[basket.basketKey];
                      const effectiveMaxHP = hpOverride ? hpOverride.maxHP : basket.maxHP;
                      const effectiveCurrentHP = hpOverride ? hpOverride.currentHP : basket.currentHP;
                      const basketHpPercent = effectiveMaxHP > 0
                        ? (effectiveCurrentHP / effectiveMaxHP) * 100
                        : 0;
                      const damageDealt = basket.damageDealt ?? 0;
                      const isConfirmEnabled = hpInputValue !== '';

                      const handleConfirm = () => {
                        const inputNum = Number(hpInputValue);
                        if (inputNum < damageDealt) {
                          window.alert(`경고: 입력값은 공격 횟수(${damageDealt.toLocaleString()}회)보다 작을 수 없습니다.`);
                          return;
                        }
                        setConfirmModal({
                          onConfirm: async () => {
                            try {
                              await setBasketHP({ eventkey: event.eventKey, inputhp: inputNum });
                            } catch (error) {
                              window.alert('HP 변경에 실패했습니다.');
                              setConfirmModal(null);
                              return;
                            }
                            setMaxHpOverrides(prev => ({
                              ...prev,
                              [basket.basketKey]: { maxHP: inputNum, currentHP: inputNum - damageDealt },
                            }));
                            LocalStorageManager.update(event.eventKey, 'baskets', 'maxHP', inputNum, true);
                            setHpPopoverBasket(null);
                            setHpInputValue('');
                            setConfirmModal(null);
                          },
                        });
                      };

                      return (
                        <div key={basket.basketKey} className="card mb-3">
                          <div className="card-body" style={{ position: 'relative' }}>
                            <h5 className="card-title">{basket.basketName}</h5>
                            <div className="progress" style={{ height: '25px', fontSize: '1rem' }}>
                              <div
                                className={`progress-bar ${getProgressBarVariant(basket.status)}`}
                                role="progressbar"
                                style={{ width: `${basketHpPercent}%` }}
                                aria-valuenow={basketHpPercent}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {basketHpPercent.toFixed(2)}%
                              </div>
                            </div>
                            <p className="text-muted mt-2 mb-1">
                              {effectiveCurrentHP.toLocaleString()} / {effectiveMaxHP.toLocaleString()}
                            </p>
                            <p className="mb-1" style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0d6efd' }}><strong>현재 시간:</strong> {currentTime.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</p>
                            {(basket.status >= 0 && basket.status <= 3) && <p className="text-dark mb-0"><strong>상태:</strong> {changeBasketStatus(basket.status)}</p>}
                            {basket.damageDealt !== undefined && <p className="text-dark mb-0"><strong>공격 횟수:</strong> {basket.damageDealt.toLocaleString()} 회</p>}
                            {basket.latitude && basket.longitude && <p className="text-dark mb-0"><GeoAltFill className="me-2" /><strong>위치:</strong> {basket.latitude}, {basket.longitude}</p>}
                            {basket.killdt && <p className="text-dark mb-0"><strong>자동 종료:</strong> {new Date(basket.killdt).toLocaleString()}</p>}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px', position: 'relative' }}>
                              {hpPopoverBasket === basket.basketKey && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 12px)',
                                  right: 0,
                                  background: 'white',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '10px',
                                  padding: '14px 16px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  minWidth: '170px',
                                  zIndex: 20,
                                }}>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ textAlign: 'center' }}
                                    placeholder="숫자 입력"
                                    value={hpInputValue}
                                    onChange={(e) => setHpInputValue(e.target.value)}
                                    autoFocus
                                  />
                                  <div className="d-flex gap-2 mt-2">
                                    <button className="btn btn-outline-warning btn-sm" title="되돌리기" disabled={!hasUndoForHp(event.eventKey)} onClick={() => setConfirmModal({ message: '되돌릴까요?', onConfirm: async () => {
                                      const d = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${event.eventKey}`));
                                      const backupHp = d?.baskets?.maxHP?.backup;
                                      if (backupHp == null) { setConfirmModal(null); return; }
                                      try {
                                        await setBasketHP({ eventkey: event.eventKey, inputhp: backupHp });
                                      } catch { window.alert('HP 되돌리기에 실패했습니다.'); setConfirmModal(null); return; }
                                      setMaxHpOverrides(prev => ({ ...prev, [basket.basketKey]: { maxHP: backupHp, currentHP: backupHp - damageDealt } }));
                                      LocalStorageManager.resetBackup(event.eventKey, 'baskets', 'maxHP');
                                      setHpPopoverBasket(null);
                                      setHpInputValue('');
                                      setConfirmModal(null);
                                    }})}>↩</button>
                                    <button className="btn btn-primary btn-sm flex-fill" disabled={!isConfirmEnabled} onClick={handleConfirm}>확인</button>
                                    <button className="btn btn-secondary btn-sm flex-fill" onClick={() => { setHpPopoverBasket(null); setHpInputValue(''); }}>취소</button>
                                  </div>
                                  <div style={{ position: 'absolute', bottom: '-9px', right: '16px', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '9px solid #dee2e6' }} />
                                  <div style={{ position: 'absolute', bottom: '-7px', right: '16px', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid white' }} />
                                </div>
                              )}
                              <button
                                className="btn btn-outline-primary btn-sm"
                                style={{ fontSize: '12px', flexShrink: 0 }}
                                onClick={() => {
                                  setHpPopoverBasket(hpPopoverBasket === basket.basketKey ? null : basket.basketKey);
                                  setHpInputValue('');
                                }}
                              >HP 수정</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="card mb-3">
                      <div className="card-header">
                        <h5 className="mb-0">이벤트 요약</h5>
                      </div>
                      <div className="card-body">
                        <p className="card-text"><PeopleFill className="me-2" /><strong>참가자:</strong> {event.participantCount} 명</p>
                        <p className="card-text"><PeopleFill className="me-2" /><strong>오늘 가입자:</strong> {event.todaySignups || 0} 명</p>
                        <p className="card-text"><PeopleFill className="me-2" /><strong>브랜드 노출 횟수:</strong> {event.brandExposure} 회</p>
                      </div>
                    </div>
                  </div>
                </div>

                {(() => {
                  const appliedInfo = eventInfoApplied[event.eventKey] || parseEventInfo(event);
                  const isEditingInfo = editingEventInfo === event.eventKey;

                  const startInfoEdit = () => {
                    setEventInfoDraft({ ...appliedInfo });
                    setEditingEventInfo(event.eventKey);
                  };
                  const cancelInfoEdit = () => setEditingEventInfo(null);
                  const applyInfoEdit = async () => {
                    try {
                      await setCampaignEventTime({
                        eventkey: event.eventKey,
                        campaignkey: campaignKey,
                        inputopendt:  `${eventInfoDraft.openDate} 00:00:00`,
                        // inputopendt:  `${eventInfoDraft.openDate} ${eventInfoDraft.openTime}`,
                        inputstartdt: `${eventInfoDraft.startDate} ${eventInfoDraft.startTime}`,
                        inputenddt:   `${eventInfoDraft.endDate} ${eventInfoDraft.endTime}`,
                        // inputclosedt: `${eventInfoDraft.closeDate} ${eventInfoDraft.closeTime}`,
                        inputclosedt: `${eventInfoDraft.closeDate} 23:59:59`,
                      });
                    } catch (error) {
                      window.alert('이벤트 일시 변경에 실패했습니다.');
                      setConfirmModal(null);
                      return;
                    }
                    setEventInfoApplied(prev => ({ ...prev, [event.eventKey]: { ...eventInfoDraft } }));
                    ['openDate', 'startDate', 'endDate', 'closeDate'].forEach(f => LocalStorageManager.resetBackup(event.eventKey, null, f));
                    if (eventInfoDraft.openDate  !== appliedInfo.openDate)  LocalStorageManager.update(event.eventKey, null, 'openDate',  `${eventInfoDraft.openDate} 00:00:00`, true);
                    if (eventInfoDraft.startDate !== appliedInfo.startDate || eventInfoDraft.startTime !== appliedInfo.startTime) LocalStorageManager.update(event.eventKey, null, 'startDate', `${eventInfoDraft.startDate} ${eventInfoDraft.startTime}`, true);
                    if (eventInfoDraft.endDate   !== appliedInfo.endDate   || eventInfoDraft.endTime   !== appliedInfo.endTime)   LocalStorageManager.update(event.eventKey, null, 'endDate',   `${eventInfoDraft.endDate} ${eventInfoDraft.endTime}`, true);
                    if (eventInfoDraft.closeDate !== appliedInfo.closeDate) LocalStorageManager.update(event.eventKey, null, 'closeDate', `${eventInfoDraft.closeDate} 23:59:59`, true);
                    setEditingEventInfo(null);
                  };

                  return (
                    <div className="row mt-4 align-items-stretch">
                      {/* 이벤트 정보 — col-md-5 (30% narrower than original col-md-7) */}
                      <div className="col-md-5 d-flex">
                        <div className="card shadow-sm flex-fill">
                          <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">이벤트 정보</h5>
                            {isEditingInfo ? (
                              <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-outline-success" title="적용완료" onClick={() => setConfirmModal({ onConfirm: async () => { await applyInfoEdit(); setConfirmModal(null); } })}>
                                  <CheckLg />
                                </button>
                                <button className="btn btn-sm btn-outline-danger" title="취소" onClick={cancelInfoEdit}>
                                  <XLg />
                                </button>
                              </div>
                            ) : (
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  title="되돌리기"
                                  disabled={!hasUndoForEventInfo(event.eventKey)}
                                  onClick={() => setConfirmModal({ message: '되돌릴까요?', onConfirm: async () => {
                                    const d = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${event.eventKey}`));
                                    const backupOpen  = d?.openDate?.backup;
                                    const backupStart = d?.startDate?.backup;
                                    const backupEnd   = d?.endDate?.backup;
                                    const backupClose = d?.closeDate?.backup;
                                    const openP  = backupOpen  ? parseDateTime(backupOpen)  : { date: appliedInfo.openDate,  time: '00:00:00' };
                                    const startP = backupStart ? parseDateTime(backupStart) : { date: appliedInfo.startDate, time: appliedInfo.startTime };
                                    const endP   = backupEnd   ? parseDateTime(backupEnd)   : { date: appliedInfo.endDate,   time: appliedInfo.endTime };
                                    const closeP = backupClose ? parseDateTime(backupClose) : { date: appliedInfo.closeDate, time: '23:59:59' };
                                    try {
                                      await setCampaignEventTime({
                                        eventkey: event.eventKey, campaignkey: campaignKey,
                                        inputopendt:  `${openP.date} 00:00:00`,
                                        inputstartdt: `${startP.date} ${startP.time}`,
                                        inputenddt:   `${endP.date} ${endP.time}`,
                                        inputclosedt: `${closeP.date} 23:59:59`,
                                      });
                                    } catch { window.alert('이벤트 정보 되돌리기에 실패했습니다.'); setConfirmModal(null); return; }
                                    setEventInfoApplied(prev => ({
                                      ...prev,
                                      [event.eventKey]: {
                                        openDate: openP.date, openTime: openP.time,
                                        startDate: startP.date, startTime: startP.time,
                                        endDate: endP.date, endTime: endP.time,
                                        closeDate: closeP.date, closeTime: closeP.time,
                                      }
                                    }));
                                    ['openDate', 'startDate', 'endDate', 'closeDate'].forEach(f => LocalStorageManager.resetBackup(event.eventKey, null, f));
                                    setConfirmModal(null);
                                  }})}
                                >↩</button>
                                <button className="btn btn-sm btn-outline-secondary" title="수정" onClick={startInfoEdit}>
                                  <PencilFill />
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="card-body">
                            {EVENT_INFO_LABELS.map(({ key, label }) => {
                              const dateVal = (isEditingInfo ? eventInfoDraft : appliedInfo)[`${key}Date`];
                              const timeVal = (isEditingInfo ? eventInfoDraft : appliedInfo)[`${key}Time`];
                              let displayStr;
                              if (key === 'open' || key === 'close'){
                                  displayStr = new Date(`${dateVal}`).toLocaleDateString();
                              } else{
                                  displayStr = new Date(`${dateVal}T${timeVal}`).toLocaleString();
                              }
                              return (
                                <div key={key} className="mb-3">
                                  <p className="card-text mb-1"><strong>{label}:</strong> {displayStr}</p>
                                  {isEditingInfo && (
                                    <div className="d-flex gap-2">
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={eventInfoDraft[`${key}Date`]}
                                        onChange={(e) => setEventInfoDraft(p => ({ ...p, [`${key}Date`]: e.target.value }))}
                                      />
                                        {key !== 'open' && key !== 'close' && (
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                step="1"
                                                value={eventInfoDraft[`${key}Time`]}
                                                onChange={(e) => setEventInfoDraft(p => ({ ...p, [`${key}Time`]: e.target.value }))}
                                            />
                                        )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      {/* 박 위치 — col-md-7 (expanded) */}
                      <div className="col-md-7 d-flex">
                        <div className="card shadow-sm flex-fill">
                          <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">박 위치</h5>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-outline-warning"
                                title="되돌리기"
                                disabled={!hasUndoForLocation(event.eventKey)}
                                onClick={() => setConfirmModal({ message: '되돌릴까요?', onConfirm: async () => {
                                  const d = JSON.parse(localStorage.getItem(`${LocalStorageManager.PREFIX}${event.eventKey}`));
                                  const basket0 = event.baskets[0];
                                  const lat      = d?.baskets?.latitude?.backup  ?? basket0.latitude;
                                  const lng      = d?.baskets?.longitude?.backup ?? basket0.longitude;
                                  const distance = d?.baskets?.distance?.backup  ?? basket0.distance;
                                  try {
                                    await setBasketLocation({ eventkey: event.eventKey, campaignkey: campaignKey, inputlatitude: lat, inputlongitude: lng, inputdistance: distance });
                                  } catch { window.alert('위치 되돌리기에 실패했습니다.'); setConfirmModal(null); return; }
                                  setBasketLocationOverrides(prev => ({ ...prev, [basket0.basketKey]: { lat, lng, distance } }));
                                  ['latitude', 'longitude', 'distance'].forEach(f => LocalStorageManager.resetBackup(event.eventKey, 'baskets', f));
                                  setConfirmModal(null);
                                }})}
                              >↩</button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title="수정"
                                onClick={() => setBasketLocationModal({ basket: event.baskets[0], eventKey: event.eventKey })}
                              >
                                <PencilFill />
                              </button>
                            </div>
                          </div>
                          <div className="card-body p-2 d-flex flex-column">
                            <div style={{ height: '210px', width: '100%' }}>
                              <TreasureMap
                                markers={event.baskets.map(b => {
                                  const ov = basketLocationOverrides[b.basketKey];
                                  return { latitude: ov ? ov.lat : b.latitude, longitude: ov ? ov.lng : b.longitude, distance: Number(ov ? ov.distance : b.distance) || null, boxName: b.basketName };
                                })}
                                circles={event.baskets.map(b => {
                                  const ov = basketLocationOverrides[b.basketKey];
                                  const dist = Number(ov ? ov.distance : b.distance) || null;
                                  if (!dist) return null;
                                  return { latitude: ov ? ov.lat : b.latitude, longitude: ov ? ov.lng : b.longitude, radius: dist, label: b.basketName };
                                }).filter(Boolean)}
                              />
                            </div>
                            <div className="mt-2 px-1">
                              {event.baskets.map(b => {
                                const ov = basketLocationOverrides[b.basketKey];
                                return (
                                  <div key={b.basketKey} className="d-flex gap-3 text-muted small mb-1">
                                    <span><strong>{b.basketName}</strong></span>
                                    <span>위도: {(ov ? ov.lat : b.latitude) ?? '-'}</span>
                                    <span>경도: {(ov ? ov.lng : b.longitude) ?? '-'}</span>
                                    <span>범위: {(Number(ov ? ov.distance : b.distance) || null) ?? '-'}{(Number(ov ? ov.distance : b.distance) || null) != null ? 'm' : ''}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <h4 className="mt-4">상품 목록</h4>
                <p className="text-muted">지정된 타수(번호)에 도달한 참여자에게 해당 상품이 지급됩니다.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="상품명 검색..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 d-flex gap-2 align-items-center">
                    <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">전체 보기</option>
                      <option value="not_given">미지급만 보기</option>
                      <option value="given">지급 완료만 보기</option>
                    </select>
                    {isItemsEditing ? (
                      <div className="d-flex gap-1 flex-shrink-0">
                        <button
                          className="btn btn-sm btn-outline-success"
                          title="적용완료"
                          onClick={() => {
                            const rawItems = event.items || event.rewards || [];
                            const changedEntries = Object.entries(itemTotalDraft).filter(([, val]) => {
                              const num = parseInt(val, 10);
                              return !isNaN(num) && num >= 0;
                            });
                            if (changedEntries.length === 0) { setIsItemsEditing(false); return; }
                            setConfirmModal({
                              onConfirm: async () => {
                                try {
                                  await Promise.all(changedEntries.map(([name, val]) => {
                                    const newTotal = parseInt(val, 10);
                                    const groupItems = rawItems.filter(item => (item.itemname || item.itemName || '미정') === name);
                                    const originalTotal = groupItems.length;
                                    const count = newTotal - originalTotal;
                                    const rewardkey = groupItems[0]?.rewardkey ?? groupItems[0]?.rewardKey;
                                    return setRewardCount({ campaignkey: campaignKey, eventkey: event.eventKey, rewardkey, count });
                                  }));
                                } catch {
                                  window.alert('상품 수량 변경에 실패했습니다.');
                                  setConfirmModal(null);
                                  return;
                                }
                                setItemTotalApplied(prev => {
                                  const next = { ...prev };
                                  changedEntries.forEach(([name, val]) => { next[name] = parseInt(val, 10); });
                                  return next;
                                });
                                setItemTotalDraft({});
                                setIsItemsEditing(false);
                                setConfirmModal(null);
                              },
                            });
                          }}
                        ><CheckLg /></button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="취소"
                          onClick={() => { setItemTotalDraft({}); setIsItemsEditing(false); }}
                        ><XLg /></button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1 flex-shrink-0">
                        <button
                          className="btn btn-sm btn-outline-warning"
                          title="되돌리기"
                          disabled={!hasUndoForItems(event.eventKey)}
                          onClick={() => setConfirmModal({ message: '되돌릴까요?', onConfirm: () => setConfirmModal(null) })}
                        >↩</button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          title="수정"
                          disabled={(event.items || event.rewards || []).length === 0}
                          onClick={() => setIsItemsEditing(true)}
                        ><PencilFill /></button>
                      </div>
                    )}
                  </div>
                </div>
                <ItemsTable
                  items={event.items || event.rewards || []}
                  searchTerm={searchTerm}
                  statusFilter={statusFilter}
                  isEditing={isItemsEditing}
                  itemTotalDraft={itemTotalDraft}
                  itemTotalApplied={itemTotalApplied}
                  onDraftChange={(name, val) => setItemTotalDraft(prev => ({ ...prev, [name]: val }))}
                />
              </div>
            )}
          </div>
        );
      })}

      {basketLocationModal && (
        <BasketLocationModal
          basket={basketLocationModal.basket}
          onClose={() => setBasketLocationModal(null)}
          onConfirm={({ lat, lng, distance }) => {
            setConfirmModal({
              onConfirm: async () => {
                try {
                  await setBasketLocation({
                    eventkey: basketLocationModal.eventKey,
                    campaignkey: campaignKey,
                    inputlatitude: lat,
                    inputlongitude: lng,
                    inputdistance: distance,
                  });
                } catch (error) {
                  window.alert('위치 변경에 실패했습니다.');
                  setConfirmModal(null);
                  return;
                }
                setBasketLocationOverrides(prev => ({
                  ...prev,
                  [basketLocationModal.basket.basketKey]: { lat, lng, distance },
                }));
                ['latitude', 'longitude', 'distance'].forEach(f => LocalStorageManager.resetBackup(basketLocationModal.eventKey, 'baskets', f));
                const prevOv   = basketLocationOverrides[basketLocationModal.basket.basketKey];
                const prevLat  = prevOv ? prevOv.lat      : basketLocationModal.basket.latitude;
                const prevLng  = prevOv ? prevOv.lng      : basketLocationModal.basket.longitude;
                const prevDist = prevOv ? prevOv.distance : basketLocationModal.basket.distance;
                if (lat      !== prevLat)  LocalStorageManager.update(basketLocationModal.eventKey, 'baskets', 'latitude',  lat,      true);
                if (lng      !== prevLng)  LocalStorageManager.update(basketLocationModal.eventKey, 'baskets', 'longitude', lng,      true);
                if (distance !== prevDist) LocalStorageManager.update(basketLocationModal.eventKey, 'baskets', 'distance',  distance, true);
                setBasketLocationModal(null);
                setConfirmModal(null);
              },
            });
          }}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          message={confirmModal.message ?? '진행하시겠습니까?\n수정된 값은 바로 반영됩니다.'}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
};

export default RankedGourdBreakingReport;
